import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import type { LazyLoadItem } from 'webapp/components/common/list/lazy-load';

type Item = LazyLoadItem;

const enum UpdateAction {
	RESET = 'reset',
	NEXT = 'next',
}

interface Args {
	allItems: Item[];
	initialItemsShown: number;
	nextItemsShown: number;
	// isHardReset?: boolean; // If true, always reset the number of visible items when a change is detected, don't try to keep the previously loaded length
	onVisibleItemsChanged?: (visibleItems: Item[]) => void;
}

interface CommonListLoadNextComponentInterface<T> {
	Args: T;
	Blocks: {
		default: [item: Item[], loadNext: () => void];
	}; // this is needed for yield
}

export default class CommonListLoadNextComponent extends Component<
	CommonListLoadNextComponentInterface<Args>
> {
	@tracked visibleItems: Item[] = [];
	@tracked currentItemsShown = 0;

	initialItemsShown = 70;
	nextItemsShown = 35;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(() => {
			this.updateInitialItemsShown();
			this.updateNextItemsShown();
			this.updateVisibleItems(UpdateAction.RESET, true);
		});
	}

	updateInitialItemsShown(shouldUpdateVisibleItems = false) {
		const oldValue = this.initialItemsShown,
			newValue =
				typeof this.args.initialItemsShown === 'number'
					? Math.abs(this.args.initialItemsShown)
					: 0;

		if (oldValue !== newValue) {
			this.initialItemsShown = newValue;

			if (shouldUpdateVisibleItems) {
				this.updateVisibleItems(UpdateAction.RESET, true);
			}
		}
	}

	updateNextItemsShown() {
		const oldValue = this.nextItemsShown,
			newValue =
				typeof this.args.nextItemsShown === 'number'
					? Math.abs(this.args.nextItemsShown)
					: 0;

		if (oldValue !== newValue) {
			this.nextItemsShown = newValue;
		}
	}

	updateVisibleItems(action: UpdateAction, forceHardReset = false) {
		const isActionReset = action === UpdateAction.RESET;

		if (this.args.allItems.length < this.currentItemsShown && !isActionReset) {
			return;
		}

		let newItemsShown = 0;

		if (isActionReset) {
			const allItemsCount = this.args.allItems.length,
				visibleItemsCount = this.visibleItems.length,
				// isHardReset = this.args.isHardReset || forceHardReset;
				isHardReset = forceHardReset;

			newItemsShown = isHardReset
				? this.initialItemsShown
				: visibleItemsCount <= allItemsCount
					? Math.max(visibleItemsCount, this.initialItemsShown)
					: allItemsCount;
		} else {
			newItemsShown = this.nextItemsShown;
			newItemsShown += this.currentItemsShown;
		}

		this.currentItemsShown = newItemsShown;
		this.visibleItems = this.args.allItems.slice(0, this.currentItemsShown);

		const onVisibleItemsChanged = this.args.onVisibleItemsChanged;
		if (onVisibleItemsChanged) {
			onVisibleItemsChanged(this.visibleItems);
		}
	}

	@action
	onInitialItemsShownChange() {
		this.updateInitialItemsShown(true);
	}

	@action
	onNextItemsShownChange() {
		this.updateNextItemsShown();
	}

	@action
	loadNext() {
		this.updateVisibleItems(UpdateAction.NEXT);
	}

	@action
	reset() {
		this.updateVisibleItems(UpdateAction.RESET);
	}
	<template>
		{{yield this.visibleItems this.loadNext}}

		<span
			class="hidden"
			{{didUpdate this.onInitialItemsShownChange @initialItemsShown}}
			{{didUpdate this.onNextItemsShownChange @nextItemsShown}}
			{{didUpdate this.reset @allItems}}
		></span>
	</template>
}
